/**
 * HomePricingSection — homepage teaser for the three real tiers. Reads
 * PRICING directly (never a hardcoded literal — see docs/PRICING_RECONCILIATION.md
 * for why that matters). Presentational only: every CTA routes to /pricing,
 * where the actual Stripe checkout flow (PricingPage.tsx) lives — this
 * component does not duplicate that logic.
 *
 * i18n note: uses two separate useTranslation calls (home + pages-products)
 * rather than the dual-namespace `useTranslation(["home", "pages-products"])`
 * form — no precedent for that form was found elsewhere in the codebase
 * (`grep -rn "useTranslation(\[" frontend/src` returned nothing), so this
 * follows the single-namespace pattern used throughout src/components/home/
 * (e.g. EvidenceLeadSection.tsx, PathsSection.tsx).
 */

import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { PRICING, fmtUSD } from "../../constants/pricing";

export default function HomePricingSection() {
  const { t } = useTranslation("home");
  const { t: tp } = useTranslation("pages-products");
  return (
    <Box id="pricing" component="section" sx={{ scrollMarginTop: "80px", textAlign: "center" }}>
      <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "secondary.main", display: "block", mb: 1 }}>
        {t("HomePricingSection.eyebrow")}
      </Typography>
      <Typography component="h2" sx={{ fontSize: 32, fontWeight: 500, m: 0, mb: 4 }}>
        {t("HomePricingSection.heading")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 2.5,
          textAlign: "left",
          maxWidth: 980,
          mx: "auto",
        }}
      >
        <Box sx={{ p: 3, borderRadius: "14px", border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
            {tp("PricingPage.freeTierName")}
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 500, mt: 1 }}>$0</Typography>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>
            {tp("PricingPage.freeTierTagline")}
          </Typography>
          <Button
            component={RouterLink}
            to="/pricing"
            variant="outlined"
            fullWidth
            sx={{ mt: 2.5, textTransform: "none", fontWeight: 500 }}
          >
            {tp("PricingPage.getStartedFreeButton")}
          </Button>
        </Box>

        <Box
          sx={{
            p: 3,
            borderRadius: "14px",
            border: 2,
            borderColor: "secondary.main",
            bgcolor: "background.paper",
            position: "relative",
          }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: 18, color: "secondary.main" }}>
            {tp("PricingPage.proTierName")}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 32, fontWeight: 500 }}>{fmtUSD(PRICING.pro.monthly)}</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {tp("PricingPage.perMonthSuffix")}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>
            {tp("PricingPage.proTierTagline")}
          </Typography>
          <Button
            component={RouterLink}
            to="/pricing"
            variant="contained"
            color="secondary"
            fullWidth
            sx={{ mt: 2.5, textTransform: "none", fontWeight: 500 }}
          >
            {tp("PricingPage.upgradeToProButton")}
          </Button>
        </Box>

        <Box sx={{ p: 3, borderRadius: "14px", border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
          <Typography sx={{ fontWeight: 500, fontSize: 18 }}>
            {tp("PricingPage.allAccessTierName")}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 32, fontWeight: 500 }}>{fmtUSD(PRICING.allAccess.monthly)}</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {tp("PricingPage.perMonthSuffix")}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>
            {tp("PricingPage.allAccessTierTagline")}
          </Typography>
          <Button
            component={RouterLink}
            to="/pricing"
            variant="outlined"
            fullWidth
            sx={{ mt: 2.5, textTransform: "none", fontWeight: 500 }}
          >
            {tp("PricingPage.getAllAccessButton")}
          </Button>
        </Box>
      </Box>

      <Typography sx={{ mt: 3, fontSize: 14, color: "text.secondary" }}>
        <Trans
          i18nKey="HomePricingSection.anchorLine"
          t={t}
          values={{ allAccessPrice: fmtUSD(PRICING.allAccess.monthly) }}
        />
      </Typography>
    </Box>
  );
}
